import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { isDailyGroupingAllowed } from "@opus-dt/shared-types";
import { useResolvedPeriod } from "./hooks";
import type {
  ClosingGridMode,
  ReportGroupBy,
  ReportPreset,
  ReportScopeKind,
} from "@/types/production-report.types";

// Spec 083 — o estado da tela mora na URL.
//
// Recorte, período, agrupamento, modo da grade e indicador principal sobrevivem ao recarregar e são
// compartilháveis por link (FR-009). É também o que permite ao assistente abrir a tela já no
// recorte e no período pedidos, sem uma segunda linguagem de estado.

const parsePreset = (v: string | null): ReportPreset =>
  v === "7d" || v === "thisMonth" || v === "lastMonth" || v === "thisYear" || v === "custom"
    ? v
    : "30d";

const parseGroupBy = (v: string | null): ReportGroupBy =>
  v === "day" || v === "month" ? v : "week";

export interface ReportState {
  scope: ReportScopeKind;
  lineId: string;
  preset: ReportPreset;
  from: string;
  to: string;
  groupBy: ReportGroupBy;
  mode: ClosingGridMode;
  indicatorId: string | null;
  /** Spec 084 — o recorte por valor de característica e a característica do quadro comparativo. */
  characteristicId: string | null;
  value: string | null;
  compareBy: string | null;
  set: (patch: Record<string, string | null>) => void;
}

export function useReportState(lockedLineId?: string): ReportState {
  const [search, setSearch] = useSearchParams();

  const custom = {
    from: search.get("from") ?? "",
    to: search.get("to") ?? "",
  };
  // Um link vindo do assistente traz `from`/`to` e NENHUM `preset` — sem esta regra o par era lido,
  // descartado e a tela abria nos últimos 30 dias, respondendo por um período que ninguém pediu.
  const preset =
    search.get("preset") === null && custom.from && custom.to
      ? "custom"
      : parsePreset(search.get("preset"));
  const resolved = useResolvedPeriod(
    preset === "custom" && (!custom.from || !custom.to) ? "30d" : preset,
    custom.from && custom.to ? custom : { from: "", to: "" }
  );

  // O agrupamento pedido pode ter deixado de ser válido quando o período cresceu — cair para
  // semana é melhor que mandar à rota algo que ela vai recusar.
  const pedido = parseGroupBy(search.get("groupBy"));
  const groupBy: ReportGroupBy =
    pedido === "day" && !isDailyGroupingAllowed(resolved.from, resolved.to) ? "week" : pedido;

  const recorteValido = Boolean(search.get("char") && search.get("value"));

  const set = React.useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(search);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      }
      setSearch(next, { replace: true });
    },
    [search, setSearch]
  );

  return {
    scope: lockedLineId ? "line" : search.get("scope") === "chain" ? "chain" : "line",
    // `lineId` é o nome que o NAV_REGISTRY monta (`nav.productionReport`); `line` é o nome legado
    // que a própria tela escrevia. Ler os dois é o que faz o link do assistente abrir a linha
    // pedida em vez de cair, em silêncio, na primeira linha da lista.
    lineId: lockedLineId ?? search.get("lineId") ?? search.get("line") ?? "",
    preset,
    from: resolved.from,
    to: resolved.to,
    groupBy,
    mode: search.get("grid") === "full" ? "full" : "summary",
    indicatorId: search.get("indicator"),
    // ⚠️ Os dois andam JUNTOS: a rota recusa um sem o outro. Um link com `char` e sem `value` abre
    // a tela sem recorte, e não em erro — a URL vem de fora (assistente, link colado) e o pior
    // resultado possível seria a tela toda virar uma mensagem de validação.
    characteristicId: recorteValido ? search.get("char") : null,
    value: recorteValido ? search.get("value") : null,
    // O quadro precisa ser de OUTRA característica (a rota também recusa).
    compareBy: search.get("compare") === search.get("char") ? null : search.get("compare"),
    set,
  };
}
